import { getLastLoginStringDisplay } from '@/utils/stringUtils';
import UserRoleBadges from './UserRoleBadges';
import { TUser, TUserActivationToggle } from '@/types/User.types';

import { TInfinitePagination, TPagination } from '@/services/api';
import CustomTable from '@/components/molecules/CustomTable';
import { useMemo } from 'react';
import { Center } from '@chakra-ui/react';
import LoadingLogo from '@/components/atoms/LoadingLogo';
import { getInvitationLabel } from '@/utils/invitationDisplay';
import { resolutionBlockedReasonLabels } from '@/utils/invitationDisplay';
import InvitationStatusBadge from './InvitationStatusBadge';

const COLUMNS_WITH_ORGANIZATION = [
  'First Name',
  'Last Name',
  'Email',
  'Role',
  'Organization',
  'Last Sign In',
  'Invitation',
  'Actions',
] as const;
const COLUMNS_WITHOUT_ORGANIZATION = [
  'First Name',
  'Last Name',
  'Email',
  'Role',
  'Last Sign In',
  'Invitation',
  'Actions',
] as const;

type TSortingColumn = (typeof COLUMNS_WITH_ORGANIZATION)[number] | (typeof COLUMNS_WITHOUT_ORGANIZATION)[number];

interface Props {
  users: TUser[] | undefined;
  pagination?: TPagination;
  infinitePagination?: TInfinitePagination;
  isLoading: boolean;
  isRefetching: boolean;
  ActionElement?: React.FunctionComponent<TUserActivationToggle>;
  invalidatedUserId?: string;
  isSearching?: boolean;
  showOrganizationColumn?: boolean;
}

const UsersTable = ({
  users,
  pagination,
  infinitePagination,
  isLoading,
  isRefetching,
  ActionElement,
  invalidatedUserId,
  isSearching,
  showOrganizationColumn = true,
}: Props) => {
  const data = {
    nodes:
      users?.map((user) => ({
        ...user,
        id: user.user_id,
      })) ?? [],
  };

  const columns = showOrganizationColumn ? COLUMNS_WITH_ORGANIZATION : COLUMNS_WITHOUT_ORGANIZATION;
  const customColumnGridPercentages = showOrganizationColumn
    ? [11, 11, 21, 16, 15, 9, 10, 7]
    : [13, 13, 25, 18, 11, 12, 8];

  const tableListItems = useMemo(
    () =>
      data.nodes.map((item: (typeof data.nodes)[number]) => {
        const isInvalidatedUser = item.id === invalidatedUserId;

        const cells: (string | React.JSX.Element)[] = [
          item.firstName ?? item.user_metadata?.firstName ?? '',
          item.lastName ?? item.user_metadata?.lastName ?? '',
          item.email ?? '/',
          <UserRoleBadges userRoles={item.roles} />,
        ];

        if (showOrganizationColumn) {
          cells.push(item.organizationName !== '' ? (item.organizationName ?? 'Org Name') : '/');
        }

        cells.push(
          getLastLoginStringDisplay(item.last_login),
          item.resolution?.state === 'BLOCKED' && item.resolution.blocked_reason ? (
            <InvitationStatusBadge
              label="Needs support"
              detail={resolutionBlockedReasonLabels[item.resolution.blocked_reason]}
            />
          ) : (
            <InvitationStatusBadge label={getInvitationLabel(item)} />
          ),
          isInvalidatedUser || !ActionElement ? '' : <ActionElement {...item} />,
        );

        return {
          id: item.user_id,
          cells,
        };
      }),
    [ActionElement, data, invalidatedUserId, showOrganizationColumn],
  );

  if (isLoading)
    return (
      <Center marginY={'100px'}>
        <LoadingLogo />
      </Center>
    );

  return (
    <CustomTable<TUser[], TSortingColumn>
      isLoading={isLoading}
      dataWithId={data.nodes}
      isRefetching={isRefetching}
      pagination={pagination}
      infinitePagination={infinitePagination}
      tableListItems={tableListItems}
      isSearching={isSearching}
      noItemsMessage="No users"
      columns={columns}
      invalidatedItemId={invalidatedUserId}
      customColumnGridPercentages={customColumnGridPercentages}
      fullWidth
      totalItemsLabel="Total Members:"
      indexToCopyToClipboard={2} // index of `item.email`
      // hasRowSelect
    />
  );
};

export default UsersTable;
